import { APP_NAME } from '@/branding';
import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSegmentationMatrix, type Finding, type MatrixCell } from '@/hooks/useSegmentationMatrix';
import { forgetRegion, readLastRegion, rememberRegion } from '@/lib/lastRegion';

/**
 * Segmentation matrix — segment × segment effective reachability.
 *
 * Cells are computed, not canned: each is the filters at every hop between two
 * segments, evaluated symbolically over both segments' whole address space
 * (`FirewallRulesAndPathCheck.md` §8.2.2). A cell that cannot be computed reads
 * `unknown` with its reason rather than guessing — reading "we could not look"
 * as "nothing filters this" would put a green square in front of somebody whose
 * traffic is being dropped.
 */
/** The finding a hover leads with: what could not be decided, else a caveat. */
function leadFinding(explanation: Finding[] | undefined): Finding | undefined {
  return explanation?.find((f) => f.effect === 'undecided') ?? explanation?.[0];
}

/** A filtered cell's label: the exception itself when one short one fits the cell. */
function filteredLabel(cell: MatrixCell): string {
  const except = cell.except ?? [];
  if (except.length === 0) return `${String(cell.services.length)} service(s)`;
  if (except.length === 1 && except[0].length <= 8) return `all but ${except[0]}`;
  return `all but ${String(except.length)}`;
}

function trafficWords(t: NonNullable<Finding['traffic']>): string {
  const what = t.ports === 'any port' ? t.protocols : `${t.protocols}, ports ${t.ports}`;
  return `${t.src} → ${t.dst} (${what})`;
}

function FindingItem({ f }: { f: Finding }) {
  return (
    <li className="text-sm">
      <div>{f.text}</div>
      {f.traffic && <div className="mt-0.5 font-mono text-[11px] text-fg-subtle">{trafficWords(f.traffic)}</div>}
      {f.devices.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1.5 text-xs">
          {f.devices.map((d) => (
            <Link key={d.id} to={`/devices/${d.id}`} className="text-primary hover:underline">
              {d.name}
            </Link>
          ))}
        </div>
      )}
    </li>
  );
}

/**
 * Why a cell says what it says (design §7.1): what could not be decided, then
 * what could still change a verdict that stands — collapsed, because on a
 * network with unread switches nearly every path carries one.
 */
function WhyPanel({ explanation }: { explanation: Finding[] }) {
  const undecided = explanation.filter((f) => f.effect === 'undecided');
  const caveats = explanation.filter((f) => f.effect === 'caveat');
  return (
    <div className="mt-3">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Why</div>
      {undecided.length > 0 && (
        <>
          <div className="mt-1.5 text-xs font-medium">What we couldn&apos;t decide</div>
          <ul className="mt-1 space-y-2">
            {undecided.map((f, i) => (
              <FindingItem key={`${f.kind}-${String(i)}`} f={f} />
            ))}
          </ul>
        </>
      )}
      {caveats.length > 0 && (
        <details className="mt-2" open={undecided.length === 0}>
          <summary className="cursor-pointer text-xs font-medium">
            What could still change this ({caveats.length})
          </summary>
          <ul className="mt-1 space-y-2 text-muted-foreground">
            {caveats.map((f, i) => (
              <FindingItem key={`${f.kind}-${String(i)}`} f={f} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default function Segmentation() {
  // The region lives in the URL, so a matrix can be linked to and Back moves
  // between regions rather than out of the page.
  const { regionId } = useParams<{ regionId?: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useSegmentationMatrix(regionId ?? null);
  const [selected, setSelected] = useState<MatrixCell | null>(null);

  // A URL naming a region that no longer resolves — deleted, or another org's —
  // 404s. Drop back to the bare path so the backend picks, and forget the
  // remembered value if it was the culprit.
  useEffect(() => {
    if (isError && regionId) {
      forgetRegion(regionId);
      // `replace`, so a dead link does not sit in history waiting for Back.
      void navigate('/segmentation', { replace: true });
    }
  }, [isError, regionId, navigate]);

  // Landing on the bare path resolves a region and rewrites the URL to it.
  //
  // This MUST replace rather than push. Pushing would leave `/segmentation` in
  // history immediately behind `/segmentation/<id>`, so Back would land there
  // and be redirected straight forward again — the two-clicks-to-go-Back bug,
  // arrived at from a third direction.
  useEffect(() => {
    if (regionId !== undefined) return; // the URL already names one
    if (!data) return;
    const target = data.regionId ?? readLastRegion() ?? data.regions[0]?.id;
    if (target) void navigate(`/segmentation/${target}`, { replace: true });
  }, [regionId, data, navigate]);

  // Remember what is actually being shown, for the next visit to the bare path.
  useEffect(() => {
    if (data?.regionId) rememberRegion(data.regionId);
  }, [data?.regionId]);

  if (isLoading || !data) {
    return (
      <div className="p-6">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Segmentation</h1>
        <Skeleton className="mt-4 h-72 w-full max-w-3xl" />
      </div>
    );
  }

  const cellFor = (from: string, to: string) => data.cells.find((c) => c.from === from && c.to === to);
  const segName = (id: string) => data.segments.find((s) => s.id === id)?.name ?? id;

  return (
    <div className="p-6 max-w-[1400px]">
      <div className="flex items-baseline gap-3">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Segmentation</h1>
        <span className="text-sm text-fg-subtle">Effective reachability between segments</span>
        {data.regions.length > 0 && (
          <select
            className="ml-auto rounded border border-border bg-transparent px-2 py-1 text-sm"
            aria-label="Region"
            value={data.regionId ?? ''}
            onChange={(e) => {
              // A push, not a replace: switching region is a navigation the
              // user should be able to undo with Back.
              void navigate(`/segmentation/${e.target.value}`);
            }}
          >
            {data.regions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        )}
        {data.mock && <Badge variant="secondary">mock cells</Badge>}
      </div>

      {data.segments.length === 0 && (
        <p className="mt-4 text-sm text-fg-subtle max-w-prose">
          {data.regions.length === 0
            ? 'This organization has no regions yet, so there is nothing to draw a matrix for.'
            : 'Nothing to compare yet in this region — define some segments, or wait for VLANs to be discovered.'}
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-6 items-start">
        <div className="overflow-x-auto">
          <table className="border-separate border-spacing-1">
            <thead>
              <tr>
                <th className="w-32" />
                {data.segments.map((s) => (
                  <th key={s.id} className="w-24 pb-1 text-xs font-semibold text-center" title={s.definition}>
                    <span className={cn(s.definition && 'cursor-help underline decoration-dotted underline-offset-2')}>{s.name}</span>
                    {s.vlanId != null && <div className="font-mono font-normal text-[10px] text-fg-subtle">VLAN {s.vlanId}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.segments.map((from) => (
                <tr key={from.id}>
                  <td className="pr-3 text-right text-xs font-semibold" title={from.definition}>
                    <span className={cn(from.definition && 'cursor-help underline decoration-dotted underline-offset-2')}>{from.name}</span>
                  </td>
                  {data.segments.map((to) => {
                    const cell = cellFor(from.id, to.id);
                    if (!cell) return <td key={to.id} />;
                    const isSelected = selected?.from === cell.from && selected.to === cell.to;
                    const lead = leadFinding(cell.explanation);
                    return (
                      <td key={to.id}>
                        <button
                          type="button"
                          onClick={() => { setSelected(cell); }}
                          title={lead?.text}
                          className={cn(
                            'relative w-24 h-16 rounded-md text-[11px] font-medium flex items-center justify-center',
                            cell.status === 'self' && 'bg-bg-subtle text-fg-subtle cursor-default',
                            cell.status === 'open' && 'bg-sev-4-soft text-sev-4',
                            cell.status === 'filtered' && 'bg-sev-3-soft text-sev-3',
                            cell.status === 'blocked' && 'bg-sev-1-soft text-sev-1',
                            cell.status === 'unknown' && 'bg-muted text-muted-foreground border border-dashed border-border',
                            isSelected && 'outline-2 outline-primary outline-offset-2 outline'
                          )}
                        >
                          {cell.status === 'self' ? '—' : cell.status === 'filtered' ? filteredLabel(cell) : cell.status}
                          {lead && (
                            <span
                              aria-label="Why"
                              className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full border border-current text-[9px] leading-none opacity-70"
                            >
                              ?
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 text-xs text-fg-subtle">Rows are sources, columns are destinations.</div>
        </div>

        {selected && selected.status !== 'self' && (
          <div className="w-96 rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold">
                {segName(selected.from)} → {segName(selected.to)}
              </div>
              <Badge variant="secondary" className="capitalize">{selected.status}</Badge>
            </div>
            {selected.except && selected.except.length > 0 ? (
              <>
                <div className="mt-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Permitted: everything except</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {selected.except.map((s) => (
                    <span key={s} className="rounded-full bg-sev-1-soft px-2.5 py-0.5 font-mono text-xs text-sev-1">{s}</span>
                  ))}
                </div>
              </>
            ) : selected.services.length > 0 && (
              <>
                <div className="mt-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Permitted</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {selected.services.map((s) => (
                    <span key={s} className="rounded-full bg-sev-4-soft px-2.5 py-0.5 font-mono text-xs text-sev-4">{s}</span>
                  ))}
                </div>
              </>
            )}
            {selected.notes && selected.notes.length > 0 && (
              // Beside what is permitted, not under "what could change it":
              // it says whom the permit is for.
              <div className="mt-3 space-y-1.5">
                {selected.notes.map((n) => (
                  <div key={n.text} role="note" className="rounded-md border border-sev-3/40 bg-sev-3-soft px-3 py-2 text-xs text-foreground">
                    {n.text}
                  </div>
                ))}
              </div>
            )}
            {selected.routes && selected.routes.length > 0 && (
              <>
                <div className="mt-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {selected.routes.length > 1 ? `Routes (${String(selected.routes.length)})` : 'Route'}
                </div>
                <div className="mt-1.5 flex flex-col gap-1.5">
                  {selected.routes.map((route, r) => (
                    <div
                      key={route.map((h) => `${h.deviceId ?? h.device}:${h.ingress ?? ''}`).join('|') || String(r)}
                      className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs"
                    >
                      {route.map((hop, i) => (
                        <Fragment key={`${hop.deviceId ?? hop.device}-${String(i)}`}>
                          {i > 0 && <span className="text-fg-subtle">→</span>}
                          <span
                            className={
                              hop.inspected
                                ? 'rounded border border-border bg-muted/40 px-1.5 py-0.5'
                                : 'rounded border border-dashed border-border px-1.5 py-0.5 text-muted-foreground'
                            }
                            title={
                              hop.inspected
                                ? undefined
                                : hop.deviceId
                                  ? `${APP_NAME} could not read this device\u2019s filter config`
                                  : `${APP_NAME} cannot match this address to a device it manages`
                            }
                          >
                            {hop.device}
                            {(hop.ingress ?? hop.egress) && (
                              <span className="ml-1 font-mono text-fg-subtle">
                                ({hop.ingress ?? '\u2026'} → {hop.egress ?? '\u2026'})
                              </span>
                            )}
                          </span>
                        </Fragment>
                      ))}
                    </div>
                  ))}
                </div>
                {selected.routes.flat().some((h) => !h.inspected) && (
                  <div className="mt-1.5 text-xs text-fg-subtle">
                    {selected.routes.flat().some((h) => !h.deviceId)
                      ? `The route leaves ${APP_NAME}\u2019s view at the dashed hop \u2014 an address ${APP_NAME} cannot match to a device it manages.`
                      : `Dashed hops carry the traffic but ${APP_NAME} has no filter config for them.`}
                  </div>
                )}
              </>
            )}
            {selected.explanation && selected.explanation.length > 0 ? (
              <WhyPanel explanation={selected.explanation} />
            ) : (
              selected.note && <div className="mt-3 text-sm text-muted-foreground">{selected.note}</div>
            )}
            <div className="mt-3 text-xs text-fg-subtle">
              Every cell is computed from parsed device config: the filters at each hop between the two segments,
              evaluated over both segments&apos; whole address space at once. A cell we cannot compute says{' '}
              <span className="font-medium">unknown</span> and gives the reason — never a guess.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
